import {
  mutationOptions,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type { Job } from '@/api/types'
import { jobPollDelay } from './polling'

/**
 * The signed-in user's recent jobs.
 *
 * Polled rather than pushed. The websocket announces a job *starting* -- the
 * `job_started` handler appends it here -- but not its progress, so the
 * percentage on a row can only come from asking. The interval is derived from
 * the data by `jobPollDelay`, which means a list with nothing running stops
 * polling by itself and starts again the moment a job is appended, with no timer
 * to remember to restart.
 *
 * Unfiltered on purpose. Asking only for unfinished jobs would drop a job out of
 * the response at the exact moment it failed, taking its error message with it.
 */
export function jobsQuery() {
  return queryOptions({
    queryKey: qk.jobs(),
    // The endpoint answers with `{"jobs": [...]}`, not a bare array. The
    // contract said otherwise until it was corrected, and this read the body as
    // a list on that promise -- so `jobPollDelay` was handed an object, threw on
    // `.filter`, and the error boundary took down every page this query is
    // mounted on. Which is all of them: the job list lives in the shell.
    queryFn: async (): Promise<Job[]> => (await unwrap(getApi().GET('/api/jobs/'))).jobs,
    refetchInterval: (query) => jobPollDelay(query.state.data ?? [], Date.now()),
    // The polling is the freshness mechanism; a refetch on mount would only
    // duplicate the first tick.
    staleTime: 1_000,
  })
}

export function useJobs() {
  return useQuery(jobsQuery())
}

export function duplicateApplicationMutation(queryClient: QueryClient) {
  return mutationOptions({
    mutationFn: (applicationId: number): Promise<Job> =>
      unwrap(
        getApi().POST('/api/applications/{application_id}/duplicate/async/', {
          params: { path: { application_id: applicationId } },
        }),
      ) as Promise<Job>,
    // Put straight into the cache rather than invalidated: the response *is* the
    // job, and the row cannot show progress until the list holds it. Writing it
    // here also restarts the polling, because the interval is derived from the
    // data and a running job is what makes it non-`false`.
    onSuccess: (job) => {
      queryClient.setQueryData<Job[]>(qk.jobs(), (current) =>
        current === undefined ? [job] : addJob(current, job),
      )
    },
  })
}

export function useDuplicateApplication() {
  return useMutation(duplicateApplicationMutation(useQueryClient()))
}

/** Appends unless it is already there, so the socket and the response agree. */
function addJob(jobs: Job[], job: Job): Job[] {
  return jobs.some((existing) => existing.id === job.id) ? jobs : [...jobs, job]
}

/**
 * Puts a job the server has just answered with into the list.
 *
 * Exported because more than one feature starts a job and none of them should
 * know how the list is stored. Written into the cache rather than invalidated,
 * for the reason above: the response *is* the job, and the poll interval is
 * derived from the data, so this is also what restarts the polling.
 */
export function pushJob(queryClient: QueryClient, job: Job): void {
  queryClient.setQueryData<Job[]>(qk.jobs(), (current) =>
    current === undefined ? [job] : addJob(current, job),
  )
}

/**
 * Copies a field, and its values if asked.
 *
 * A job rather than a request, because copying the values of a column on a
 * large table is not something to hold a connection open for. The copy arrives
 * the way a duplicated application does -- over the websocket, as a
 * `field_created` -- so nothing here waits for it; what the job is for is
 * saying how far along it is while it runs.
 */
export function duplicateFieldMutation(queryClient: QueryClient) {
  return mutationOptions({
    mutationFn: ({
      fieldId,
      withData,
    }: {
      fieldId: number
      withData: boolean
    }): Promise<Job> =>
      unwrap(
        getApi().POST('/api/database/fields/{field_id}/duplicate/async/', {
          params: { path: { field_id: fieldId } },
          body: { duplicate_data: withData } as never,
        }),
      ) as Promise<Job>,
    // Straight into the cache, for the reason a duplicated application is: the
    // response *is* the job, nothing can show progress until the list holds it,
    // and writing it restarts the polling, since the interval is derived from
    // the data.
    onSuccess: (job) => {
      queryClient.setQueryData<Job[]>(qk.jobs(), (current) =>
        current === undefined ? [job] : addJob(current, job),
      )
    },
  })
}
